import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, FileText, Image as ImageIcon, Loader2, CheckCircle2, Trash2, Clock } from "lucide-react";
import { submitOrder } from "../lib/api";

const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];
const MAX_SIZE = 25 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const UPS_DOMAIN_REGEX = /@theupsstore\.com$/i;

const formatUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const OrderSheet = ({
  open,
  onClose,
  sessionId,
  sessionEmail,
  quote,
  chatTranscript,
  fatheadIds,
  onSuccess,
}) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(sessionEmail || "");
  const [storeNumber, setStoreNumber] = useState("");
  const [files, setFiles] = useState([]);
  const [artworkLater, setArtworkLater] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  if (!open) return null;

  const isUpsStaff = UPS_DOMAIN_REGEX.test((sessionEmail || email || "").trim());

  const handleFiles = (newFiles) => {
    const accepted = [];
    for (const f of newFiles) {
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${f.name}: only PDF, JPG, JPEG, PNG allowed`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: exceeds 25MB`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const errs = {};
    if (!fullName.trim()) errs.fullName = "Full name required";
    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      errs.email = "Enter a valid email address.";
    }
    if (isUpsStaff && !storeNumber.trim()) {
      errs.storeNumber = "Store number required for UPS Store staff";
    }
    if (!artworkLater && files.length === 0 && !(fatheadIds && fatheadIds.length)) {
      errs.files = "Attach at least one artwork file, or check 'I'll share artwork later'.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("full_name", fullName.trim());
      fd.append("email", email.trim());
      fd.append("store_number", isUpsStaff ? storeNumber.trim() : "");
      fd.append("retail_price", String(quote?.retail_price ?? 0));
      fd.append("ups_price", String(quote?.ups_price ?? 0));
      fd.append("product_description", chatTranscript || "");
      fd.append("is_custom_quote", String(Boolean(quote?.is_custom_quote)));
      fd.append("custom_note", String(quote?.custom_note || ""));
      fd.append("artwork_later", String(Boolean(artworkLater)));
      if (quote?.lines && quote.lines.length) {
        fd.append("quote_lines", JSON.stringify(quote.lines));
      }
      if (fatheadIds && fatheadIds.length) {
        fd.append("fathead_ids", fatheadIds.join(","));
      }
      // Skip file attachments entirely if customer chose to share artwork later
      if (!artworkLater) {
        files.forEach((f) => fd.append("files", f));
      }

      const res = await submitOrder(fd);
      toast.success(`Order #${res.order_number} submitted.`);
      onSuccess(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit order";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="order-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={submitting ? undefined : onClose} />
      <div
        className="relative ml-auto w-full sm:max-w-xl bg-[color:var(--ups-paper)] h-full overflow-y-auto border-l-2 border-[color:var(--ups-ink)] shadow-[-8px_0_0_0_#FFB500]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[color:var(--ups-ink)] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "#FFB500" }}>Step 02 · Submit Order</div>
            <h3 className="font-display text-xl font-bold mt-0.5">Order Details</h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 hover:bg-white/10 rounded-sm transition"
            aria-label="Close"
            data-testid="close-order-sheet"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="order-form">
          {/* Quote summary */}
          {quote && (
            <div className="p-4 card-swiss rounded-sm" style={{ borderLeft: "4px solid var(--ups-gold)" }}>
              <div className="overline mb-1">
                {quote.lines && quote.lines.length ? `Combined Quote · ${quote.lines.length} items` : "Quoted Product"}
              </div>
              {quote.lines && quote.lines.length ? (
                <ul className="mt-1 space-y-1.5 text-[13px]" data-testid="order-sheet-line-items">
                  {quote.lines.map((line, idx) => (
                    <li key={idx} className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="font-bold">{line.quantity || 0} × </span>
                        <span>{line.product_name}</span>
                        {line.is_custom_quote && (
                          <span
                            className="ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                            style={{ background: "#FFB500", color: "var(--ups-ink)" }}
                          >
                            Custom · PM
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[12px] shrink-0" style={{ color: "var(--ups-accent)" }}>
                        {Number(line.ups_price || 0) === 0 && Number(line.retail_price || 0) === 0
                          ? "TBQ"
                          : formatUSD(line.ups_price)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <>
                  <div className="font-display text-base font-bold">{quote.product_name}</div>
                  {quote.specifications && (
                    <div className="text-sm text-[color:var(--ups-subtext)] mt-1">{quote.specifications}</div>
                  )}
                </>
              )}
              <div className="flex gap-6 mt-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase font-bold text-[color:var(--ups-subtext)]">
                    {quote.lines && quote.lines.length ? "Combined Retail" : "Retail"}
                  </div>
                  <div className="line-through text-[color:var(--ups-subtext)]">{formatUSD(quote.retail_price)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase font-bold" style={{ color: "var(--ups-accent)" }}>
                    {quote.lines && quote.lines.length ? "Combined Your Price" : "Your Price"}
                  </div>
                  <div className="font-bold" style={{ color: "var(--ups-accent)" }}>{formatUSD(quote.ups_price)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Full Name</label>
            <input
              className="input-swiss w-full px-4 py-3 rounded-sm"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              data-testid="order-fullname-input"
            />
            {errors.fullName && <p className="text-xs text-red-700 mt-1">{errors.fullName}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Email</label>
            <input
              type="email"
              className="input-swiss w-full px-4 py-3 rounded-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              data-testid="order-email-input"
            />
            {errors.email && <p className="text-xs text-red-700 mt-1">{errors.email}</p>}
          </div>

          {/* Store Number — UPS Store staff only */}
          {isUpsStaff && (
            <div data-testid="order-store-number-section">
              <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Store Number</label>
              <input
                className="input-swiss w-full px-4 py-3 rounded-sm"
                value={storeNumber}
                onChange={(e) => setStoreNumber(e.target.value)}
                placeholder="e.g. 3423"
                data-testid="order-store-number-input"
              />
              {errors.storeNumber && <p className="text-xs text-red-700 mt-1">{errors.storeNumber}</p>}
            </div>
          )}

          {/* Artwork */}
          <div>
            <div className="flex items-start justify-between gap-3 mb-2">
              <label className="block text-xs font-bold uppercase tracking-[0.18em]">
                Artwork Attachments
                {fatheadIds && fatheadIds.length > 0 && (
                  <span className="ml-2 font-mono font-normal text-[10px] text-[color:var(--ups-subtext)]">
                    ({fatheadIds.length} Big Head artwork already attached)
                  </span>
                )}
              </label>
              <label
                className="flex items-center gap-2 text-[11px] font-mono cursor-pointer select-none"
                style={{ color: "var(--ups-subtext)" }}
                data-testid="artwork-later-toggle"
              >
                <input
                  type="checkbox"
                  checked={artworkLater}
                  onChange={(e) => {
                    setArtworkLater(e.target.checked);
                    if (e.target.checked && errors.files) {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                    }
                  }}
                  data-testid="artwork-later-checkbox"
                />
                I&apos;ll share artwork later
              </label>
            </div>

            {artworkLater ? (
              <div
                className="rounded-sm p-4 flex items-start gap-3 text-[12px]"
                style={{ background: "#FFFBEB", border: "2px dashed var(--ups-ink)" }}
                data-testid="artwork-later-banner"
              >
                <Clock size={18} className="shrink-0 mt-0.5" style={{ color: "#92400E" }} />
                <div>
                  <div className="font-bold uppercase tracking-[0.14em] text-[10px]" style={{ color: "#92400E" }}>
                    Artwork later
                  </div>
                  <div className="mt-1 text-[color:var(--ups-ink)] leading-snug">
                    No worries — submit the order now and we&apos;ll follow up with you to collect the
                    artwork. Production starts when artwork lands.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded-sm p-6 text-center cursor-pointer transition ${
                    dragActive ? "bg-[color:var(--ups-muted)]" : "bg-white"
                  }`}
                  style={{
                    border: `2px dashed ${dragActive ? "var(--ups-ink)" : "var(--ups-border)"}`,
                  }}
                  data-testid="file-dropzone"
                >
                  <Upload size={22} className="mx-auto mb-2 text-[color:var(--ups-subtext)]" />
                  <div className="text-sm font-medium">Drag &amp; drop or click to browse</div>
                  <div className="text-xs text-[color:var(--ups-subtext)] mt-1 font-mono">
                    PDF · JPG · JPEG · PNG · max 25MB each
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                    data-testid="file-input"
                  />
                </div>
                {errors.files && <p className="text-xs text-red-700 mt-1">{errors.files}</p>}
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="file-list">
                    {files.map((f, idx) => {
                      const isPdf = f.name.toLowerCase().endsWith(".pdf");
                      return (
                        <li
                          key={`${f.name}-${idx}`}
                          className="flex items-center justify-between gap-3 px-3 py-2 card-swiss rounded-sm"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {isPdf ? <FileText size={18} /> : <ImageIcon size={18} />}
                            <div className="min-w-0">
                              <div className="text-sm truncate">{f.name}</div>
                              <div className="text-[11px] font-mono text-[color:var(--ups-subtext)]">
                                {formatBytes(f.size)}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="p-1.5 hover:bg-[color:var(--ups-muted)] rounded-sm"
                            aria-label={`Remove ${f.name}`}
                            data-testid={`remove-file-${idx}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Transcript preview (auto-filled, read-only) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold uppercase tracking-[0.18em]">
                Product Description (auto-filled)
              </label>
              <span className="text-[10px] font-mono text-[color:var(--ups-subtext)] flex items-center gap-1">
                <CheckCircle2 size={12} /> from chat
              </span>
            </div>
            <textarea
              className="input-swiss w-full px-4 py-3 rounded-sm font-mono text-xs leading-relaxed"
              rows={6}
              readOnly
              value={chatTranscript || ""}
              data-testid="order-transcript"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-brutal w-full py-3 px-5 rounded-sm flex items-center justify-center gap-2 text-base"
            data-testid="submit-order-button"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Submitting & emailing…
              </>
            ) : (
              <>Submit Order</>
            )}
          </button>
          <p className="text-[11px] font-mono text-center text-[color:var(--ups-subtext)]">
            An email confirmation will be sent to you and our print floor.
          </p>
        </form>
      </div>
    </div>
  );
};

export default OrderSheet;
